import { Box } from '@mui/material';
import { useEffect, useRef } from 'react';
import { useIntl } from 'react-intl';
import { CleanContoursIcon, InterpolateIcon } from '~/assets/workspace-icons';
import { useRecoilState } from 'recoil';
import FloatingPanel from '~/components/toolbar/shared/FloatingPanel';
import ToolbarButton from '~/components/toolbar/shared/ToolbarButton';
import { buildToolTooltip } from '~/components/toolbar/shared/toolTooltip';
import { useStructureToolGating } from '~/components/toolbar/shared/useStructureToolGating';
import { openFloatingPanelState } from '~/state/atoms/toolsAtoms';
import ClearContoursInline from './slice/ClearContoursInline';
import InterpolationOperation from './slice/InterpolationOperation';

export default function SliceOperationsSection() {
    // Clear and interpolate modify the selected ROI's contours in place.
    const { selectedRoiContourEditDisabled: isDisabled, isSelectionApproved } =
        useStructureToolGating();
    const intl = useIntl();

    const approvedReason = isSelectionApproved
        ? intl.formatMessage({ id: 'tool.disabled.approved' })
        : undefined;

    const clearAnchorRef = useRef<HTMLElement | null>(null);
    const interpolateAnchorRef = useRef<HTMLElement | null>(null);

    // Disabling the ToolbarButton doesn't close an already-open panel.
    const [openPanel, setOpenPanel] = useRecoilState(openFloatingPanelState);
    useEffect(() => {
        if (
            isDisabled &&
            (openPanel === 'interpolation' || openPanel === 'clear')
        ) {
            setOpenPanel(null);
        }
    }, [isDisabled, openPanel, setOpenPanel]);

    return (
        <Box
            sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: 0.25,
            }}
        >
            <ToolbarButton
                icon={<InterpolateIcon />}
                tooltip={buildToolTooltip(
                    intl,
                    intl.formatMessage({ id: 'toolbar.interpolation.tooltip' }),
                    approvedReason
                )}
                tooltipPlacement="left"
                disabled={isDisabled}
                panelId="interpolation"
                anchorRef={interpolateAnchorRef}
            />
            <FloatingPanel
                panelId="interpolation"
                anchorEl={interpolateAnchorRef.current}
                placement="left-start"
            >
                <InterpolationOperation />
            </FloatingPanel>
            <ToolbarButton
                icon={<CleanContoursIcon />}
                tooltip={buildToolTooltip(
                    intl,
                    intl.formatMessage({ id: 'toolbar.clearContours.tooltip' }),
                    approvedReason
                )}
                tooltipPlacement="left"
                disabled={isDisabled}
                panelId="clear"
                anchorRef={clearAnchorRef}
            />
            <FloatingPanel
                panelId="clear"
                anchorEl={clearAnchorRef.current}
                placement="left-start"
            >
                <ClearContoursInline />
            </FloatingPanel>
        </Box>
    );
}
